import React from 'react';
import { ClienteConsolidado } from '../types';
import { formatCurrency, formatKwh, formatPercent } from '../utils';
import { Target, Zap, Users, TrendingUp, AlertCircle, Shield } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

interface Props {
  clientes: ClienteConsolidado[];
  totalAberto: number;
}

const BRAND = {
  primary: '#5F6C37',
  secondary: '#F6A937',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  green: '#10b981',
  blue: '#3b82f6',
};

const VisaoExecutiva: React.FC<Props> = ({ clientes, totalAberto }) => {
  const totalFaturas = clientes.reduce((s, c) => s + c.qtdFaturas, 0);
  const saldoTotal = clientes.reduce((s, c) => s + c.saldoAcumulado, 0);
  
  const extClientes = clientes.filter(c => !c.interno);
  const intClientes = clientes.filter(c => c.interno);

  const totalAbertoExt = extClientes.reduce((s, c) => s + c.valorTotal, 0);
  const totalAbertoInt = intClientes.reduce((s, c) => s + c.valorTotal, 0);

  const top10 = clientes.slice(0, 10);
  const top10Val = top10.reduce((s, c) => s + c.valorTotal, 0);
  const top10Pct = totalAberto > 0 ? top10Val / totalAberto : 0;

  const riscoData = [
    { name: 'Estratégico Alto', value: extClientes.filter(c => c.risco === 'RISCO ESTRATÉGICO ALTO').reduce((s, c) => s + c.valorTotal, 0), color: BRAND.red },
    { name: 'Alto', value: extClientes.filter(c => c.risco === 'RISCO ALTO').reduce((s, c) => s + c.valorTotal, 0), color: BRAND.orange },
    { name: 'Médio', value: extClientes.filter(c => c.risco === 'RISCO MÉDIO').reduce((s, c) => s + c.valorTotal, 0), color: BRAND.amber },
    { name: 'Baixo', value: extClientes.filter(c => c.risco === 'RISCO BAIXO').reduce((s, c) => s + c.valorTotal, 0), color: BRAND.green },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Visão Executiva da Carteira</h2>
          <p className="text-sm text-gray-500">Resumo consolidado e exposição ao risco financeiro</p>
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Exposição Total</span>
            <AlertCircle size={18} className="text-red-400" />
          </div>
          <p className="text-3xl font-black text-gray-800">{formatCurrency(totalAberto)}</p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-gray-500">{totalFaturas} faturas abertas</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#5F6C37]"></div>
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Clientes Inadimplentes</span>
            <Users size={18} className="text-[#5F6C37]" />
          </div>
          <p className="text-3xl font-black text-gray-800">{clientes.length}</p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-gray-500">{extClientes.length} externos / {intClientes.length} internos</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#F6A937]"></div>
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Concentração (Top 10)</span>
            <Target size={18} className="text-[#F6A937]" />
          </div>
          <p className="text-3xl font-black text-gray-800">{formatPercent(top10Pct * 100)}</p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-gray-500">{formatCurrency(top10Val)} concentrado</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Saldo Acumulado Retido</span>
            <Zap size={18} className="text-blue-400" />
          </div>
          <p className="text-3xl font-black text-gray-800">{formatKwh(saldoTotal)}</p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-gray-500">Energia bloqueada passível de distrato</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Distribuição Interno x Externo */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Composição da Inadimplência</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 font-semibold">Clientes Externos</span>
                <span className="font-bold text-gray-800">{formatCurrency(totalAbertoExt)}</span>
              </div>
              <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: `${totalAberto > 0 ? (totalAbertoExt/totalAberto)*100 : 0}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 font-semibold">Unidades Internas / Rateio</span>
                <span className="font-bold text-gray-800">{formatCurrency(totalAbertoInt)}</span>
              </div>
              <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#5F6C37] rounded-full" style={{ width: `${totalAberto > 0 ? (totalAbertoInt/totalAberto)*100 : 0}%` }}></div>
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-start gap-3">
              <Shield className="text-[#5F6C37] mt-0.5" size={16} />
              <p className="text-xs text-gray-600 leading-relaxed">
                As unidades internas não representam risco sistêmico de perda, mas impactam o fluxo de caixa. O foco das ações extrajudiciais deve estar nos clientes externos.
              </p>
            </div>
          </div>
        </div>

        {/* Gráfico de Risco */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm col-span-2">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Exposição por Nível de Risco (Externos)</h3>
          <div className="flex h-56">
            <div className="w-1/2 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={riscoData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {riscoData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-1/2 flex flex-col justify-center gap-3">
              {riscoData.map((r, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }}></div>
                      <span className="font-semibold text-gray-700">{r.name}</span>
                    </div>
                    <span className="font-bold text-gray-800">{formatCurrency(r.value)}</span>
                  </div>
                  <div className="pl-4 text-[10px] text-gray-400">
                    {formatPercent((r.value / totalAbertoExt) * 100)} do risco externo
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default VisaoExecutiva;
